<template>
  <div class="onboarding-signup">
    <div v-if="$wait.is('Register')" class="onboarding-signup__loading-screen">
      <div class="onboarding-signup__loading-screen-inner">
        <p class="onboarding-signup__loading-text">Creating account</p>
        <ButtonSpinner />
      </div>
    </div>

    <div class="onboarding-signup__banner">
      <button
        v-if="isTemporaryUser"
        class="onboarding-signup__back-15807"
        @click="router.back()"
      >
        <span class="iconfont iback"></span> Back
      </button>

      <div>
        <div class="onboarding-signup__logo-wrapper">
          <img
            class="onboarding-signup__logo"
            src="@/assets.app/images/logo.svg"
            alt="Shortform App"
          />
        </div>
      </div>

      <Slider />
    </div>

    <div class="onboarding-signup__form--tablet">
      <BasePopup ref="bottomSheetRef" class="onboarding-signup__sheet">
        <SignupSheet
          :view="view"
          :showNameInput="!passwordlessSignup"
          :passwordless="passwordlessSignup"
        />
      </BasePopup>
      <div class="onboarding-signup__buttons">
        <GoogleSignInButton
          v-if="socialView?.googleAuthView && showGoogleSignupOnMobile"
          :authMode="authMode"
          buttonLayout="mobile-stacked"
          :view="socialView.googleAuthView"
          showButton="big"
        />
        <AppleSignInButton
          v-if="socialView?.appleAuthView"
          class="onboarding-signup__button--apple"
          :view="socialView.appleAuthView"
          title="Sign up with"
        />
        <FacebookLoginButton
          v-if="socialView?.facebookAuthView"
          title="Sign up with"
          :view="socialView.facebookAuthView"
          class="onboarding-signup__button--facebook"
        >
        </FacebookLoginButton>
        <button
          class="button shortform-sign-up__button onboarding-signup__button--email"
          @click="openInfoSheet"
        >
          <img
            class="onboarding-signup__button-image"
            src="@/assets.app/images/onboarding/email.svg"
            alt=""
          />
          <span class="onboarding-signup__button-text">Sign up with Email</span>
        </button>
      </div>

      <p class="onboarding-signup__links">
        By creating an account, you agree to the
        <router-link :to="{ name: 'site_terms' }">Terms of Service</router-link>
        and
        <router-link :to="{ name: 'site_privacy' }">Privacy Policy</router-link
        >.
      </p>

      <div class="onboarding-signup__login">
        Already have an account?
        <router-link :to="{ name: 'login', query: route.query }"
          >Log in</router-link
        >
      </div>
    </div>

    <div v-if="view" class="sign-up onboarding-signup__form--desktop">
      <div class="sign-up__form-box">
        <div class="shortform-sign-up">
          <h2 class="shortform-sign-up__title">Sign Up to Shortform</h2>
          <form
            class="shortform-sign-up__form"
            role="form"
            novalidate
            @submit.prevent="startEmailSignup"
          >
            <div class="shortform-sign-up__group">
              <label class="shortform-sign-up__label" for="register_email"
                >Email Address</label
              >
              <InputWithContextMenu
                id="register_email"
                v-model="view.model.email"
                class="shortform-sign-up__input"
                :class="view.validation.stateClass('email')"
                type="email"
                placeholder=""
                autofocus
              />
              <FormError :errors="view.validation.errors" field="email" />
            </div>
            <div v-if="!passwordlessSignup" class="shortform-sign-up__group">
              <label class="shortform-sign-up__label" for="register_password"
                >Password</label
              >
              <InputWithContextMenu
                id="register_password"
                v-model="view.model.password"
                type="password"
                autocomplete="new-password"
                placeholder=""
                class="shortform-sign-up__input"
                :class="view.validation.stateClass('password')"
              />
              <FormError :errors="view.validation.errors" field="password" />
            </div>
            <div
              v-if="!passwordlessSignup && view.model.password_score"
              class="text-muted font-italic"
            >
              <small>
                password strength:
                <span
                  v-if="view.model.password_score === '4'"
                  class="text-success font-weight-700"
                  >strong</span
                >
                <span
                  v-if="view.model.password_score === '3'"
                  class="text-info font-weight-700"
                  >good</span
                >
                <span
                  v-if="view.model.password_score === '2'"
                  class="text-warning font-weight-700"
                  >medium</span
                >
                <span
                  v-if="view.model.password_score === '1'"
                  class="text-danger font-weight-700"
                  >weak</span
                >
                <span
                  v-if="view.model.password_score === '0'"
                  class="text-danger font-weight-700"
                  >terrible</span
                >
              </small>
            </div>
            <div
              v-if="view.validation.errors && view.validation.errors._schema"
              class="alert alert-danger"
            >
              {view.validation.errors._schema}
            </div>
            <div
              class="shortform-sign-up__group shortform-sign-up__group--terms"
            >
              <span class="shortform-sign-up__terms"
                >By creating an account, you agree to the
                <router-link
                  :to="{ name: 'site_terms' }"
                  class="black"
                  target="_blank"
                  >Terms of Service</router-link
                >
                and
                <router-link
                  :to="{ name: 'site_privacy' }"
                  class="black"
                  target="_blank"
                  >Privacy Policy</router-link
                >.
              </span>
            </div>
            <div
              class="shortform-sign-up__group shortform-sign-up__group--submit"
            >
              <v-wait for="Register" style="display: flex">
                <template v-slot:waiting>
                  <div class="shortform-sign-up__submit button button--theme">
                    Creating Account
                    <button-spinner />
                  </div>
                </template>
                <button
                  class="shortform-sign-up__submit button button--theme"
                  type="submit"
                >
                  Create Account
                </button>
              </v-wait>
            </div>
          </form>
          <AuthPartition mobileText="Sign up with" />
          <SocialAuth
            v-if="socialView"
            class="shortform-sign-up__social-auth"
            :view="socialView"
            :authMode="authMode"
            googleButtonLayout="signup-desktop"
            :showGoogle="!showGoogleSignupOnMobile"
          />
          <p class="shortform-sign-up__log-in">
            Already have an account?
            <router-link :to="loginRoute" style="color: #ffb600"
              ><strong>Log in</strong></router-link
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
} from 'vue'
import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router'

import { useSignupAuth } from '@/models/auth/useSignupAuth'
import { GoogleAuthView } from '@/models/auth/google'
import { auth } from '@/services/auth'
import { authCssFix } from '@/helpers/html'
import { waitTimer } from '@/helpers/timer'
import { TABLET_WIDTH } from '@/init/settings'
import { passwordlessSignup18304Variant } from '@/services/ab/experiments'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import SocialAuth from '@/app/components/auth/SocialAuth.vue'
import AuthPartition from '@/app/components/auth/AuthPartition.vue'
import AppleSignInButton from '@/app/components/auth/AppleSignInButton.vue'
import FacebookLoginButton from '@/app/components/auth/FacebookLoginButton.vue'
import GoogleSignInButton from '@/app/components/auth/GoogleSignInButton.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import BasePopup from '@/components/ui/BasePopup.vue'
import SignupSheet from '../signup/SignupSheet.vue'
import Slider from '../signup/Slider.vue'

const props = defineProps<{
  nextOnboardingPage: () => void
}>()

const route = useRoute()
const router = useRouter()
const {
  signupView: view,
  socialView,
  authMode,
  initSocialAuth,
  prefillEmail,
  startEmailSignup,
} = useSignupAuth({
  onSuccess: async (nextPage: RouteLocationRaw | null): Promise<void> => {
    const { timeout, routeTarget } = getPostAuthRedirect(nextPage ?? undefined)

    await waitTimer(timeout)

    if (routeTarget) {
      await router.push(routeTarget)
    } else {
      props.nextOnboardingPage()
    }
  },
})

const bottomSheetRef = useTemplateRef('bottomSheetRef')

const showGoogleSignupOnMobile = ref(false)

const passwordlessSignup = computed(
  () => passwordlessSignup18304Variant() === 'B',
)

function updateShowGoogleSignupOnMobile(): void {
  showGoogleSignupOnMobile.value = window.innerWidth <= TABLET_WIDTH

  if (
    socialView.value &&
    socialView.value.googleAuthView instanceof GoogleAuthView
  ) {
    const googleAuthView = socialView.value.googleAuthView as GoogleAuthView
    setTimeout(() => googleAuthView.renderButtons(), 100)
  }
}

function openInfoSheet(): void {
  bottomSheetRef.value?.show()
}

/**
 * Resolve the post-auth redirect target and delay for onboarding signup.
 */
function getPostAuthRedirect(nextPage?: RouteLocationRaw): {
  timeout: number
  routeTarget: RouteLocationRaw | null
} {
  // Timeout before the redirect to let Mixpanel calls complete.
  // It is less problematic in the main app, where Mixpanel would track
  // missing events on the next page, but in the extension, the next page
  // is the extension auth page, which closes immediately and Mixpanel
  // calls are lost, so we set a longer timeout in this case.
  // See #9509.
  if (router.currentRoute.value.query?.redirect === '/extension-auth') {
    return {
      routeTarget: { name: 'extension_auth' },
      timeout: 3000,
    }
  }

  return {
    routeTarget: nextPage ?? null,
    timeout: 100,
  }
}

onBeforeMount(async () => {
  await initSocialAuth()

  updateShowGoogleSignupOnMobile()
  window.addEventListener('resize', updateShowGoogleSignupOnMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateShowGoogleSignupOnMobile)
})

onMounted(async () => {
  // If user name was passed a prop (from the signup with email form)
  if (route.query.email) {
    prefillEmail(route.query.email as string)
  }

  authCssFix()
})

const loginRoute = computed(() => {
  const routeData = { name: 'login', query: route.query }
  // If we have the email set, pass it on to the signup page.
  if (view.value.model.email) {
    routeData.query.email = view.value.model.email
  }
  return routeData
})

const isTemporaryUser = computed(() => {
  return auth.isTemporaryUser()
})
</script>

<style lang="scss" scoped>
.onboarding-signup {
  width: 100%;
  height: 100%;
  display: flex;
  background: $c-white;

  @media only screen and (max-width: $w-tablet-s) {
    flex-direction: column;
  }

  &__logo-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;

    @media only screen and (max-width: $w-tablet-s) {
      padding-block: 40px;
    }
    @media only screen and (max-width: $w-mobile-s) {
      padding-block: 20px;
    }

    /* Short viewport (e.g. iPhone SE) – reduce padding */
    @media only screen and (max-width: $w-mobile-s) and (max-height: 668px) {
      padding-block: 4px;
    }
  }

  &__banner {
    display: flex;
    padding: 22px;
    padding-top: 56px;
    background: $c-bg;
    flex-direction: column;
    position: relative;

    align-items: center;
    justify-content: space-between;
    width: 560px;
    height: 100%;
    @supports (padding-top: env(safe-area-inset-top)) {
      padding-top: max(56px, env(safe-area-inset-top));
    }

    @media only screen and (max-width: $w-tablet-s) {
      width: 100%;
      min-height: 0;
      align-items: inherit;
      border-bottom-left-radius: 20px;
      border-bottom-right-radius: 20px;

      @media only screen and (max-height: 811px) {
        padding-top: 22px;
        @supports (padding-top: env(safe-area-inset-top)) {
          padding-top: max(22px, env(safe-area-inset-top));
        }
      }

      @media only screen and (max-height: 714px) {
        padding-bottom: 12px;
      }
    }
  }

  &__back-15807 {
    width: fit-content;
    margin-bottom: 8px;
    font-size: 16px;
    color: $c-black-3;
  }

  &__form {
    &--tablet {
      display: none;
      @media only screen and (max-width: $w-tablet-s) {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
        padding: var(--onboarding-padding);
        padding-bottom: 0;

        @media only screen and (max-height: 714px) {
          padding-top: 12px;
        }
      }
    }

    &--desktop {
      display: block;
      flex-grow: 1;

      @media only screen and (max-width: $w-tablet-s) {
        display: none;
      }
    }
  }

  &__links {
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    max-width: 280px;
    margin-inline: auto;
    color: $c-black-6;
    margin-top: 32px;

    @media only screen and (max-width: $w-tablet-s) {
      margin: 0;
      padding-inline: 12px;
    }
    @media only screen and (max-width: $w-medium-s) {
      padding-inline: 0px;
    }

    a {
      color: $c-black-3;
    }
  }

  &__buttons {
    --mobile-social-button-width: 327px;
    --mobile-social-button-height: 44px;
    margin-top: 24px;
    gap: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: var(--onboarding-inner-width);
    margin: 0;
    flex-direction: column;

    @media only screen and (max-width: $w-tablet-s) {
      margin: 0;
    }

    :deep(.shortform-sign-up__button),
    .onboarding-signup__button--email {
      width: var(--mobile-social-button-width);
      max-width: 100%;
      margin: 0;
    }

    // Reserve the Google slot height up-front so the column doesn't jump
    // while GSI injects the button asynchronously.
    :deep(.googleSigninBig) {
      height: var(--mobile-social-button-height);
      overflow: hidden;
    }

    :deep(.shortform-sign-up__button),
    .onboarding-signup__button--email {
      height: var(--mobile-social-button-height);
      min-height: var(--mobile-social-button-height);
      justify-content: center;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      line-height: 32px;
      letter-spacing: -0.28px;
    }

    :deep(.shortform-sign-up__button) {
      display: inline-flex;
      align-items: center;
      padding: 0 16px;
      border-radius: 100px;
    }

    .onboarding-signup__button--email {
      color: $c-black-2;
      border: 1px solid #d5d8e0;
      background: #f8f8f8;
      box-shadow:
        0px 10px 10px rgba(20, 28, 49, 0.03),
        0px 2px 6px rgba(0, 0, 0, 0.03);
    }

    .onboarding-signup__button-image {
      width: 24px;
      height: 24px;
    }

    .onboarding-signup__button-text {
      display: inline;
      font-size: 14px;
      line-height: 32px;
      letter-spacing: -0.28px;
    }

    :deep(.button__img) {
      margin-bottom: 2px;
    }

    :deep(.facebookSignIn .button__img) {
      width: 9px;
      height: 19px;
    }
  }

  &__button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 56px;
    height: 56px;
    border: 1px solid $c-border-3;
    border-radius: 50%;
    filter: drop-shadow(0px 1px 5px rgba(255, 255, 255, 0.1))
      drop-shadow(0px 8px 15px rgba(20, 28, 49, 0.1));
    background: $c-white;

    &--apple {
      background-color: $c-black-1;
      color: $c-white;
    }
  }

  &__button--facebook {
    border-color: transparent;
    background: #3b5998;
    color: $c-white;

    :deep(.button__img) {
      @media only screen and (max-width: $w-tablet-s) {
        width: 14px;
      }
    }
  }

  &__sheet {
    :deep(.popup__window) {
      background: $c-bg;
      padding: 0;

      &::before {
        display: none;
      }
    }

    :deep(.popup__close) {
      display: none;
    }
  }

  &__login {
    background: $c-bg;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    margin-inline: calc(var(--onboarding-padding) * -1);
    width: calc(100% + (var(--onboarding-padding) * 2));
    padding: 24px var(--onboarding-padding);
    padding-bottom: calc(
      var(--onboarding-padding) + env(safe-area-inset-bottom, 0px)
    );
    font-size: 16px;
    line-height: 23px;
    text-align: center;
    color: $c-black-15;
    a {
      color: $c-yellow;
    }
    @media only screen and (max-width: $w-tablet-s) {
      margin: 0;
    }
  }

  .sign-up {
    .shortform-sign-up {
      display: flex;
      flex-direction: column;
      max-width: 360px;

      .shortform-sign-up__title {
        font-size: 32px;
        line-height: 36px;
        margin-top: 64px;
        margin-bottom: 24px;
      }

      .shortform-sign-up__social-auth {
        order: 2;

        :deep(.googleSigninBig) {
          margin-bottom: 16px;
          width: 360px;
        }

        :deep(.googleSigninBig > div) {
          border-color: $c-black-6;
        }

        :deep(.shortform-sign-up__other-way .shortform-sign-up__button) {
          margin-bottom: 16px;
        }

        :deep(
          .shortform-sign-up__other-way .shortform-sign-up__button:last-child
        ) {
          margin-bottom: 0;
        }
      }

      :deep(.shortform-sign-up__partition) {
        order: 3;
        margin: 24px 0;
        z-index: 2;
      }

      .shortform-sign-up__form {
        order: 4;
        margin-bottom: 0;
      }

      .shortform-sign-up__label {
        font-size: 16px;
        line-height: 20px;
        margin-bottom: 8px;
      }

      .shortform-sign-up__input {
        height: 44px;
        font-size: 16px;
        line-height: 20px;
      }

      :deep(.shortform-sign-up__button) {
        max-width: 360px;
        width: 360px;
      }

      .shortform-sign-up__group--terms {
        margin-bottom: 24px;
      }

      .shortform-sign-up__terms {
        display: block;
        font-size: 16px;
        line-height: 20px;
      }

      .shortform-sign-up__group--submit {
        margin-bottom: 0;
      }

      .shortform-sign-up__submit {
        margin-top: 0;
        width: 360px;
        height: 40px;
        font-size: 16px;
        line-height: 20px;
        padding: 10px 20px;
      }

      .shortform-sign-up__log-in {
        margin-top: 40px;
        order: 5;
      }
    }

    .sign-up__form-box {
      width: auto;
      margin-top: 64px;
    }
  }

  &__loading-screen {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 999;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    animation: fadeIn 0.5s ease;
    @media only screen and (min-width: $w-tablet-b) {
      display: none;
    }
  }

  &__loading-screen-inner {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 16px 32px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.8);
  }

  &__loading-text {
    color: $c-white;
    margin: 0;
  }
}
</style>
